import { createSlice, createAsyncThunk, PayloadAction, nanoid } from '@reduxjs/toolkit';
import {
  SiteLayoutState,
  ProcessPathConfig,
  ProcessPathState,
  WorkstationState,
  StaffingMode,
  SaveStatus,
  ValidationErrors,
  GetSiteLayoutConfigResponse,
  SaveSiteLayoutConfigRequest,
  SaveSiteLayoutConfigResponse,
} from '../../pages/settings/tabs/siteLayout/types';
import {
  getSiteLayoutConfig,
  getAvailableProcessPaths,
  saveSiteLayoutConfig as saveSiteLayoutConfigApi,
} from '../../pages/settings/tabs/siteLayout/api';

// ─── Initial State ──────────────────────────────────────────────────────────────

const initialState: SiteLayoutState = {
  availableProcessPaths: [],
  availableProcessPathsStatus: 'idle',
  processPaths: [],
  lastModifiedBy: null,
  lastModifiedAt: null,
  loadStatus: 'idle',
  saveStatus: 'idle',
  errorMessage: null,
  hasUnsavedChanges: false,
};

// ─── Async Thunks ───────────────────────────────────────────────────────────────

export const fetchSiteLayoutConfig = createAsyncThunk<
  GetSiteLayoutConfigResponse,
  string,
  { rejectValue: string }
>(
  'siteLayout/fetchSiteLayoutConfig',
  async (warehouseId: string, { rejectWithValue }) => {
    try {
      const response = await getSiteLayoutConfig(warehouseId);
      return response;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch site layout config';
      return rejectWithValue(message);
    }
  }
);

export const fetchAvailableProcessPaths = createAsyncThunk<
  ProcessPathConfig[],
  string,
  { rejectValue: string }
>(
  'siteLayout/fetchAvailableProcessPaths',
  async (warehouseId: string, { rejectWithValue }) => {
    try {
      const response = await getAvailableProcessPaths(warehouseId);
      return response.processPathConfigs;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch available process paths';
      return rejectWithValue(message);
    }
  }
);

export const saveSiteLayoutConfig = createAsyncThunk<
  SaveSiteLayoutConfigResponse,
  SaveSiteLayoutConfigRequest,
  { rejectValue: string }
>(
  'siteLayout/saveSiteLayoutConfig',
  async (request: SaveSiteLayoutConfigRequest, { rejectWithValue, dispatch }) => {
    try {
      const response = await saveSiteLayoutConfigApi(request);
      // Reload config after successful save
      dispatch(fetchSiteLayoutConfig(request.warehouseId));
      return response;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to save site layout config';
      return rejectWithValue(message);
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────────────────────────

const siteLayoutSlice = createSlice({
  name: 'siteLayout',
  initialState,
  reducers: {
    addProcessPath(state, action: PayloadAction<ProcessPathConfig>) {
      const config = action.payload;
      const newProcessPath: ProcessPathState = {
        processId: config.processId,
        processPathName: config.processPathName,
        departmentName: config.departmentName,
        functionId: config.functionId,
        functionName: config.functionName,
        functionType: config.functionType,
        staffingMode: 'PROCESS_LINES',
        lines: [],
        individualWorkstations: [],
      };
      state.processPaths.push(newProcessPath);
      state.hasUnsavedChanges = true;
    },

    removeProcessPath(state, action: PayloadAction<number>) {
      const processPathIndex = action.payload;
      if (state.processPaths[processPathIndex]) {
        state.processPaths.splice(processPathIndex, 1);
        state.hasUnsavedChanges = true;
      }
    },

    setStaffingMode(state, action: PayloadAction<{ processPathIndex: number; mode: StaffingMode }>) {
      const { processPathIndex, mode } = action.payload;
      const processPath = state.processPaths[processPathIndex];
      if (processPath) {
        if (processPath.staffingMode === mode) return;
        processPath.staffingMode = mode;
        if (mode === 'DEPARTMENT') {
          processPath.lines = [];
        } else {
          processPath.individualWorkstations = [];
        }
        state.hasUnsavedChanges = true;
      }
    },

    addLine: {
      reducer(state, action: PayloadAction<{ processPathIndex: number; lineId: string }>) {
        const { processPathIndex, lineId } = action.payload;
        const processPath = state.processPaths[processPathIndex];
        if (processPath) {
          processPath.lines.push({
            lineId,
            lineName: '',
            lineCapacity: 1,
            workstations: [],
          });
          state.hasUnsavedChanges = true;
        }
      },
      prepare(payload: { processPathIndex: number }) {
        return { payload: { ...payload, lineId: nanoid() } };
      },
    },

    removeLine(state, action: PayloadAction<{ processPathIndex: number; lineIndex: number }>) {
      const { processPathIndex, lineIndex } = action.payload;
      const processPath = state.processPaths[processPathIndex];
      if (processPath && processPath.lines[lineIndex]) {
        processPath.lines.splice(lineIndex, 1);
        state.hasUnsavedChanges = true;
      }
    },

    updateLineName(state, action: PayloadAction<{ processPathIndex: number; lineIndex: number; name: string }>) {
      const { processPathIndex, lineIndex, name } = action.payload;
      const processPath = state.processPaths[processPathIndex];
      if (processPath && processPath.lines[lineIndex]) {
        processPath.lines[lineIndex].lineName = name;
        state.hasUnsavedChanges = true;
      }
    },

    addWorkstation(state, action: PayloadAction<{
      processPathIndex: number;
      lineIndex?: number;
      workstationId: string;
    }>) {
      const { processPathIndex, lineIndex, workstationId } = action.payload;
      if (!workstationId || workstationId.trim() === '') return;

      const processPath = state.processPaths[processPathIndex];
      if (!processPath) return;

      const workstation: WorkstationState = {
        id: nanoid(),
        workstationId: workstationId.trim(),
        enabled: true,
      };

      if (lineIndex !== undefined) {
        const line = processPath.lines[lineIndex];
        if (line) {
          line.workstations.push(workstation);
          line.lineCapacity = line.workstations.length;
          state.hasUnsavedChanges = true;
        }
      } else {
        processPath.individualWorkstations.push(workstation);
        state.hasUnsavedChanges = true;
      }
    },

    removeWorkstation(state, action: PayloadAction<{
      processPathIndex: number;
      lineIndex?: number;
      workstationIndex: number;
    }>) {
      const { processPathIndex, lineIndex, workstationIndex } = action.payload;
      const processPath = state.processPaths[processPathIndex];
      if (!processPath) return;

      if (lineIndex !== undefined) {
        const line = processPath.lines[lineIndex];
        if (line && line.workstations[workstationIndex]) {
          line.workstations.splice(workstationIndex, 1);
          line.lineCapacity = line.workstations.length;
          state.hasUnsavedChanges = true;
        }
      } else {
        if (processPath.individualWorkstations[workstationIndex]) {
          processPath.individualWorkstations.splice(workstationIndex, 1);
          state.hasUnsavedChanges = true;
        }
      }
    },

    toggleWorkstation(state, action: PayloadAction<{
      processPathIndex: number;
      lineIndex?: number;
      workstationIndex: number;
      enabled: boolean;
    }>) {
      const { processPathIndex, lineIndex, workstationIndex, enabled } = action.payload;
      const processPath = state.processPaths[processPathIndex];
      if (!processPath) return;

      if (lineIndex !== undefined) {
        const line = processPath.lines[lineIndex];
        if (line && line.workstations[workstationIndex]) {
          line.workstations[workstationIndex].enabled = enabled;
          state.hasUnsavedChanges = true;
        }
      } else {
        if (processPath.individualWorkstations[workstationIndex]) {
          processPath.individualWorkstations[workstationIndex].enabled = enabled;
          state.hasUnsavedChanges = true;
        }
      }
    },

    renameWorkstation(state, action: PayloadAction<{
      processPathIndex: number;
      lineIndex?: number;
      workstationIndex: number;
      workstationId: string;
    }>) {
      const { processPathIndex, lineIndex, workstationIndex, workstationId } = action.payload;
      const processPath = state.processPaths[processPathIndex];
      if (!processPath) return;

      if (lineIndex !== undefined) {
        const line = processPath.lines[lineIndex];
        if (line && line.workstations[workstationIndex]) {
          line.workstations[workstationIndex].workstationId = workstationId;
          state.hasUnsavedChanges = true;
        }
      } else {
        if (processPath.individualWorkstations[workstationIndex]) {
          processPath.individualWorkstations[workstationIndex].workstationId = workstationId;
          state.hasUnsavedChanges = true;
        }
      }
    },

    setWorkstations(state, action: PayloadAction<{
      processPathIndex: number;
      lineIndex?: number;
      workstations: WorkstationState[];
    }>) {
      const { processPathIndex, lineIndex, workstations } = action.payload;
      const processPath = state.processPaths[processPathIndex];
      if (!processPath) return;

      if (lineIndex !== undefined) {
        const line = processPath.lines[lineIndex];
        if (line) {
          line.workstations = workstations;
          state.hasUnsavedChanges = true;
        }
      } else {
        processPath.individualWorkstations = workstations;
        state.hasUnsavedChanges = true;
      }
    },

    setSaveStatus(state, action: PayloadAction<SaveStatus>) {
      state.saveStatus = action.payload;
    },

    setSaveError(state, action: PayloadAction<string>) {
      state.saveStatus = 'error';
      state.errorMessage = action.payload;
    },

    setSaveValidationErrors(state, action: PayloadAction<ValidationErrors>) {
      state.saveStatus = 'error';
      state.errorMessage = Object.values(action.payload).join('; ');
    },

    clearUnsavedChanges(state) {
      state.hasUnsavedChanges = false;
    },
  },
  extraReducers: (builder) => {
    // fetchSiteLayoutConfig lifecycle
    builder
      .addCase(fetchSiteLayoutConfig.pending, (state) => {
        state.loadStatus = 'loading';
        state.errorMessage = null;
      })
      .addCase(fetchSiteLayoutConfig.fulfilled, (state, action) => {
        state.loadStatus = 'success';
        const { siteLayout, lastModifiedBy, lastModifiedAt } = action.payload;
        state.processPaths = siteLayout?.savedProcessPaths ?? [];
        state.lastModifiedBy = lastModifiedBy;
        state.lastModifiedAt = lastModifiedAt;
        state.hasUnsavedChanges = false;
      })
      .addCase(fetchSiteLayoutConfig.rejected, (state, action) => {
        state.loadStatus = 'error';
        state.errorMessage = action.payload ?? 'Unknown error';
      });

    // fetchAvailableProcessPaths lifecycle
    builder
      .addCase(fetchAvailableProcessPaths.pending, (state) => {
        state.availableProcessPathsStatus = 'loading';
      })
      .addCase(fetchAvailableProcessPaths.fulfilled, (state, action) => {
        state.availableProcessPathsStatus = 'success';
        state.availableProcessPaths = action.payload;
      })
      .addCase(fetchAvailableProcessPaths.rejected, (state, action) => {
        state.availableProcessPathsStatus = 'error';
        state.availableProcessPaths = [];
      });

    // saveSiteLayoutConfig lifecycle
    builder
      .addCase(saveSiteLayoutConfig.pending, (state) => {
        state.saveStatus = 'saving';
        state.errorMessage = null;
      })
      .addCase(saveSiteLayoutConfig.fulfilled, (state) => {
        state.saveStatus = 'success';
        state.hasUnsavedChanges = false;
      })
      .addCase(saveSiteLayoutConfig.rejected, (state, action) => {
        state.saveStatus = 'error';
        state.errorMessage = action.payload ?? 'Save failed';
      });
  },
});

// ─── Exports ────────────────────────────────────────────────────────────────────

export const {
  addProcessPath,
  removeProcessPath,
  setStaffingMode,
  addLine,
  removeLine,
  updateLineName,
  addWorkstation,
  removeWorkstation,
  toggleWorkstation,
  renameWorkstation,
  setWorkstations,
  setSaveStatus,
  setSaveError,
  setSaveValidationErrors,
  clearUnsavedChanges,
} = siteLayoutSlice.actions;

export default siteLayoutSlice.reducer;

// ─── Selectors ──────────────────────────────────────────────────────────────────

export const selectSiteLayoutState = (state: { siteLayout: SiteLayoutState }) =>
  state.siteLayout;

export const selectProcessPaths = (state: { siteLayout: SiteLayoutState }) =>
  state.siteLayout.processPaths;

export const selectAvailableProcessPaths = (state: { siteLayout: SiteLayoutState }) =>
  state.siteLayout.availableProcessPaths;

export const selectHasUnsavedChanges = (state: { siteLayout: SiteLayoutState }) =>
  state.siteLayout.hasUnsavedChanges;

export const selectSiteLayoutLoadStatus = (state: { siteLayout: SiteLayoutState }) =>
  state.siteLayout.loadStatus;

export const selectSiteLayoutSaveStatus = (state: { siteLayout: SiteLayoutState }) =>
  state.siteLayout.saveStatus;
